import React, { useState, useEffect, useMemo } from "react";
import {
    useTheme,
    InputAdornment,
    Box,
    Button,
    Card,
    CardContent,
    CardActions,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Paper,
    Autocomplete,
    Pagination,
    IconButton,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Avatar,
    Grid,
} from "@mui/material";
import Tooltip from '@mui/material/Tooltip';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import VerifiedIcon from "@mui/icons-material/Verified";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

import LocationOnIcon from '@mui/icons-material/LocationOn';
import CategoryIcon from '@mui/icons-material/Category';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import { Helmet } from "react-helmet";
import { useUser } from "../context/UserContext";
import SectionLoader from "../components/SectionLoader";
import Toast from "../components/Toast";
import BlockIcon from '@mui/icons-material/Block';
import SettingsIcon from '@mui/icons-material/Settings';
import ModuleSettingsModal from "../components/ModuleSettingsModal";
import DOMPurify from "dompurify";

const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
};



function StatCard({ label, count, IconComponent, active = false, onClick }) {
    return (
        <Card
            onClick={onClick}
            sx={{
                height: "100%",
                minWidth: { xs: '100%', sm: 200 },
                width: "100%",
                borderRadius: 2,
                cursor: onClick ? 'pointer' : 'default',
                background: active
                    ? 'linear-gradient(135deg, rgba(76, 161, 175, 0.4) 0%, rgba(44, 83, 100, 0.4) 100%)'
                    : 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: active ? '1px solid #4CA1AF' : '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: active ? '0 8px 32px rgba(76, 161, 175, 0.25)' : '0 4px 20px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.25)',
                    borderColor: 'rgba(76, 161, 175, 0.5)',
                },
            }}
        >
            <CardContent sx={{ minHeight: 80, p: 2 }}>
                <Box display="flex" alignItems="center" sx={{ width: "100%" }}>
                    <Box
                        sx={{
                            height: 48,
                            width: 48,
                            backgroundColor: active ? '#4CA1AF' : 'rgba(76, 161, 175, 0.15)',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2,
                            flexShrink: 0,
                            transition: 'all 0.25s ease',
                            boxShadow: active ? '0 4px 12px rgba(76, 161, 175, 0.4)' : 'none',
                        }}
                    >
                        <IconComponent sx={{ fontSize: 28, color: active ? '#FFFFFF' : '#4CA1AF' }} />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                            <Typography variant="h5" fontWeight="bold" noWrap sx={{ color: '#FFFFFF' }}>
                                {count}
                            </Typography>
                            {active && (
                                <Box
                                    sx={{
                                        px: 1,
                                        py: 0.2,
                                        borderRadius: 1,
                                        bgcolor: 'rgba(76, 161, 175, 0.3)',
                                        border: '1px solid rgba(76, 161, 175, 0.6)',
                                        color: '#64d2e2',
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    Active
                                </Box>
                            )}
                        </Box>
                        <Typography variant="body2" noWrap sx={{ color: active ? '#FFFFFF' : 'rgba(255,255,255,0.7)', fontWeight: active ? 600 : 400 }}>
                            {label}
                        </Typography>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}

export default function NewItemRequests({ hideHeader = false }) {
    const { user, isSwitched, effectiveOrgId } = useUser();
    const muiTheme = useTheme();
    const isLight = muiTheme.palette.mode === 'light';
    const [sortOrder, setSortOrder] = useState('asc');
    const [loading, setLoading] = useState(true);
    const [requests, setRequests] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [organization, setOrganization] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
    const [settingsOpen, setSettingsOpen] = useState(false);

    // Filter states
    const [filterCategory, setFilterCategory] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [filterLocation, setFilterLocation] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    // Statistics
    const [totalRequests, setTotalRequests] = useState(0);
    const [pendingRequests, setPendingRequests] = useState(0);
    const [givenRequests, setGivenRequests] = useState(0);
    const [acceptedRequests, setAcceptedRequests] = useState(0);
    const [cancelledRequests, setCancelledRequests] = useState(0);

    // View dialog
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);

    /////// Block User Dialog
    const [openBlockDialog, setOpenBlockDialog] = useState(false);
    const [userToBlock, setUserToBlock] = useState(null);

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/admin/';
    const orgOptions = [{ id: 0, org_name: "All Organizations" }, ...organizations];
    // Categories for filter
    const categories = [
        "Electronics",
        "Books",
        "Clothes",
        "Furniture",
        "Toys",
        "Kitchen Items",
        "Sports Equipment",
        "Others"
    ];

    const statuses = ["Pending", "Given", "Accepted", "Cancelled"];

    useEffect(() => {
        fetchOrganizations();
    }, []);

    useEffect(() => {
        fetchCounts();
    }, [effectiveOrgId, organization]);

    useEffect(() => {
        fetchRequests();
    }, [page, filterCategory, filterStatus, filterLocation, searchQuery, organization, effectiveOrgId, fromDate, toDate]);

    const fetchOrganizations = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}roles-organizations`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            const data = await response.json();

            if (!response.ok || data.success === false) {
                if (data.message === 'Invalid token') {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                    return;
                }
                setToast({ open: true, message: data?.message || 'Failed to update organizations', severity: "error" });
            } else {
                setOrganizations(data.organizations || []);
            }
        } catch (error) {
            console.error('Error fetching organizations:', error);
        }
    };

    const fetchCounts = async () => {
        try {
            const token = localStorage.getItem('token');
            const targetOrg = effectiveOrgId || (organization && organization.id !== 0 ? organization.id : null);
            const requestBody = {};
            if (targetOrg) requestBody.org_id = targetOrg;

            const response = await fetch(`${API_URL}giver-item-requests`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });
            const data = await response.json();
            if (data && Array.isArray(data.data)) {
                const all = data.data;
                setTotalRequests(all.length);
                setPendingRequests(all.filter(req => req.status === 'Pending').length);
                setGivenRequests(all.filter(req => req.status === 'Given').length);
                setAcceptedRequests(all.filter(req => req.status === 'Accepted').length);
                setCancelledRequests(all.filter(req => req.status === 'Cancelled').length);
            }
        } catch (error) {
            console.error('Error fetching request counts:', error);
        }
    };

    const fetchRequests = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const targetOrg = effectiveOrgId || (organization && organization.id !== 0 ? organization.id : null);

            // Build request body
            const requestBody = {};
            if (filterCategory) requestBody.category = filterCategory;
            if (filterStatus) requestBody.status = filterStatus;
            if (filterLocation) requestBody.location = filterLocation;
            if (searchQuery) requestBody.search = searchQuery;
            if (targetOrg) requestBody.org_id = targetOrg;

            const response = await fetch(`${API_URL}giver-item-requests`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });

            const data = await response.json();

            if (!response.ok || data.success === false) {
                if (data.message === 'Invalid token') {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                    return;
                }
                setToast({ open: true, message: data?.message || 'Failed to update organizations', severity: "error" });
            } else {
                let requestsData = Array.isArray(data?.data) ? data.data : [];
                if (fromDate) {
                    const from = new Date(fromDate);
                    requestsData = requestsData.filter(req => new Date(req.created_at) >= from);
                }
                if (toDate) {
                    const to = new Date(toDate);
                    to.setHours(23, 59, 59, 999);
                    requestsData = requestsData.filter(req => new Date(req.created_at) <= to);
                }

                setRequests(requestsData);

                // Calculate pagination (12 items per page)
                const itemsPerPage = 12;
                setTotalPages(Math.ceil(requestsData.length / itemsPerPage));
            }
        } catch (error) {
            console.error('Error fetching requests:', error);
            setToast({ open: true, message: 'Network error. Please try again.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleViewRequest = (request) => {
        setSelectedRequest(request);
        setOpenViewDialog(true);
    };

    const handleViewDialogClose = () => {
        setOpenViewDialog(false);
        setSelectedRequest(null);
    };



    const handleClearFilters = () => {
        setFilterCategory("");
        setFilterStatus("");
        setFilterLocation("");
        setSearchQuery("");
        setOrganization(null);
        setPage(1);
    };

    // Paginate requests
    const itemsPerPage = 12;
    const paginatedRequests = requests.slice((page - 1) * itemsPerPage, page * itemsPerPage);


    const handleBlockUnblockUser = (userId, currentBlockedStatus, userName) => {
        setUserToBlock({ userId, currentBlockedStatus, userName });
        setOpenBlockDialog(true);
    };

    const confirmBlockUnblockUser = async () => {
        try {
            const { userId, currentBlockedStatus } = userToBlock;
            const newBlockedStatus = currentBlockedStatus === 'yes' ? 'no' : 'yes';
            const token = localStorage.getItem('token');

            const response = await fetch(`${API_URL}block-unblock-user`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: userId,
                    blocked_status: newBlockedStatus
                }),
            });

            const data = await response.json();

            if (!response.ok || data.success === false) {
                if (data.message === 'Invalid token') {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                    return;
                }

                setToast({ open: true, message: data?.message || 'Failed to update user status', severity: "error" });
            } else {
                setToast({
                    open: true,
                    message: newBlockedStatus === 'yes' ? 'User blocked successfully' : 'User unblocked successfully',
                    severity: 'success'
                });
                fetchRequests();
            }

            setOpenBlockDialog(false);
            setUserToBlock(null);

        } catch (error) {
            console.error('Error blocking/unblocking user:', error);
            setToast({ open: true, message: 'Network error. Please try again.', severity: 'error' });
            setOpenBlockDialog(false);
            setUserToBlock(null);
        }
    };


    const handleUpdateStatus = async (requestId, newStatus) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}update-item-request-status`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: requestId,
                    status: newStatus
                }),
            });

            const data = await response.json();

            if (data.success && data.status === 200) {
                setToast({ open: true, message: `Request ${newStatus.toLowerCase()} successfully`, severity: 'success' });
                fetchRequests();
            } else {
                setToast({ open: true, message: data.message || 'Failed to update status', severity: 'error' });
            }
        } catch (error) {
            console.error('Error updating status:', error);
            setToast({ open: true, message: 'Network error. Please try again.', severity: 'error' });
        }
    };
    return (
        <>
            {!hideHeader && (
                <Helmet>
                    <title>New Item Requests - GPN</title>
                </Helmet>
            )}

            <Box p={hideHeader ? 0 : { xs: 1, sm: 2, md: 3 }}>
                {/* Header */}
                {!hideHeader && (
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h5" fontWeight="bold">
                            New Item Requests
                        </Typography>
                    </Box>
                )}

                                {/* Compact Unified Toolbar */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 1.5,
                        mb: 2.5,
                        borderRadius: 2.5,
                        background: isLight ? "#FFFFFF" : "rgba(255,255,255,0.04)",
                        border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255,255,255,0.08)",
                        backdropFilter: "blur(20px)",
                    }}
                >
                    {/* Row 1: Title, Count, Search, Sort & Actions */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 1.5,
                            pb: 1.25,
                            borderBottom: isLight ? "1px solid #F1F5F9" : "1px solid rgba(255,255,255,0.06)",
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
                                New Requests
                            </Typography>
                            <Chip
                                label={
                                filterStatus === "Pending"
                                    ? `${pendingRequests} Pending`
                                    : filterStatus === "Given"
                                    ? `${givenRequests} Given`
                                    : filterStatus === "Accepted"
                                    ? `${acceptedRequests} Accepted`
                                    : `${totalRequests || requests.length} Total`
                            }
                                size="small"
                                sx={{
                                    height: 22,
                                    fontSize: "0.75rem",
                                    fontWeight: 700,
                                    bgcolor: isLight ? "rgba(13, 148, 136, 0.1)" : "rgba(76, 161, 175, 0.2)",
                                    color: isLight ? "#0D9488" : "#64d2e2",
                                }}
                            />
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <TextField
                                size="small"
                                placeholder="Search requests..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)", fontSize: 18 }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ width: { xs: "100%", sm: 220 }, "& .MuiInputBase-input": { py: 0.6, fontSize: "0.85rem" } }}
                            />

                            <Select
                                size="small"
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                                displayEmpty
                                renderValue={(val) => `Sort: ${val === 'asc' ? 'A → Z' : val === 'desc' ? 'Z → A' : 'Newest'}`}
                                sx={{ width: 130, "& .MuiSelect-select": { py: 0.6, fontSize: "0.85rem" } }}
                            >
                                <MenuItem value="asc">Item: A → Z (Alphabetical)</MenuItem>
                                <MenuItem value="desc">Item: Z → A</MenuItem>
                                <MenuItem value="newest">Date: Newest First</MenuItem>
                            </Select>

                            <Button
                                size="small"
                                variant="outlined"
                                onClick={handleClearFilters}
                                sx={{ textTransform: "none", fontSize: "0.8rem", py: 0.5, px: 1.2 }}
                            >
                                Clear
                            </Button>

                            {!hideHeader && (
                                <Tooltip title="Settings">
                                    <IconButton size="small" onClick={() => setSettingsOpen(true)} sx={{ color: "#64d2e2" }}>
                                        <SettingsIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>

                    {/* Row 2: Status Triage Chips & Secondary Filters */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 1.25,
                            pt: 1.25,
                        }}
                    >
                        {/* Status Triage Chips */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                            {[
                                { key: "", label: "All", count: totalRequests || requests.length },
                                { key: "Pending", label: "Pending", count: pendingRequests },
                                { key: "Given", label: "Given", count: givenRequests },
                                { key: "Accepted", label: "Accepted", count: acceptedRequests },
                            ].map((tab) => {
                                const isActive = (tab.key === "" && (filterStatus === "" || filterStatus === "All")) || filterStatus === tab.key;
                                return (
                                    <Chip
                                        key={tab.label}
                                        label={`${tab.label} (${tab.count})`}
                                        size="small"
                                        onClick={() => {
                                            setFilterStatus(tab.key);
                                            setPage(1);
                                        }}
                                        sx={{
                                            cursor: "pointer",
                                            fontWeight: isActive ? 700 : 500,
                                            fontSize: "0.78rem",
                                            height: 26,
                                            bgcolor: isActive
                                                ? (isLight ? "#0D9488" : "#4CA1AF")
                                                : (isLight ? "#F1F5F9" : "rgba(255,255,255,0.06)"),
                                            color: isActive ? "#FFFFFF" : (isLight ? "#475569" : "rgba(255,255,255,0.7)"),
                                            "&:hover": {
                                                bgcolor: isActive
                                                    ? (isLight ? "#0F766E" : "#3b8b98")
                                                    : (isLight ? "#E2E8F0" : "rgba(255,255,255,0.12)"),
                                            },
                                        }}
                                    />
                                );
                            })}
                        </Box>

                        {/* Secondary Filters */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            {user?.role === 1 && !isSwitched && (
                                <Autocomplete
                                    size="small"
                                    value={organization}
                                    onChange={(event, newValue) => {
                                        setOrganization(newValue);
                                        setPage(1);
                                    }}
                                    options={orgOptions}
                                    getOptionLabel={(option) => option?.org_name || ""}
                                    renderInput={(params) => <TextField {...params} placeholder="Organization" />}
                                    sx={{ width: 170, "& .MuiInputBase-input": { py: "2px !important", fontSize: "0.8rem" } }}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                />
                            )}
                        </Box>
                    </Box>
                </Paper>

                {/* Loading State */}
                {loading && <SectionLoader open={loading} />}

                {/* Requests Table */}
                <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{
                        borderRadius: 2.5,
                        overflow: 'hidden',
                        border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(76, 161, 175, 0.25)',
                        bgcolor: isLight ? '#FFFFFF' : 'rgba(255, 255, 255, 0.02)',
                        boxShadow: isLight ? '0 2px 10px rgba(0, 0, 0, 0.04)' : '0 4px 20px rgba(0, 0, 0, 0.25)',
                        width: '100%',
                    }}
                >
                    <Table sx={{ minWidth: { xs: 0, lg: 900 } }}>
                        <TableHead sx={{ bgcolor: isLight ? '#F1F5F9' : '#1a2636' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, color: isLight ? '#334155' : '#94A3B8', width: 60, py: 1.8 }}>#</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: isLight ? '#334155' : '#94A3B8' }}>Item Requested</TableCell>
                                <TableCell className="col-tablet-hide" sx={{ fontWeight: 700, color: isLight ? '#334155' : '#94A3B8' }}>Category</TableCell>
                                <TableCell className="col-tablet-hide" sx={{ fontWeight: 700, color: isLight ? '#334155' : '#94A3B8' }}>Delivery Method</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: isLight ? '#334155' : '#94A3B8' }}>Requester</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: isLight ? '#334155' : '#94A3B8' }}>Item Status</TableCell>
                                <TableCell className="col-tablet-hide" sx={{ fontWeight: 700, color: isLight ? '#334155' : '#94A3B8' }}>Admin Status</TableCell>
                                <TableCell className="col-tablet-hide" sx={{ fontWeight: 700, color: isLight ? '#334155' : '#94A3B8', width: 110 }}>Date</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700, color: isLight ? '#334155' : '#94A3B8', width: 140 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedRequests.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                                        <Typography color="text.secondary" variant="h6" fontWeight={600} gutterBottom>
                                            No requests found
                                        </Typography>
                                        <Typography color="text.secondary" variant="body2">
                                            Try adjusting your filters or search query.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedRequests.map((request, index) => {
                                    const rowNum = (page - 1) * itemsPerPage + index + 1;
                                    const userBlocked = request.app_users?.blocked_status === 'yes';

                                    return (
                                        <TableRow
                                            key={request.id}
                                            hover
                                            onClick={() => handleViewRequest(request)}
                                            sx={{
                                                cursor: 'pointer',
                                                transition: 'background-color 0.15s ease',
                                                '&:hover': {
                                                    bgcolor: isLight ? '#F0FDFA !important' : 'rgba(13, 148, 136, 0.1) !important',
                                                },
                                                '& td': {
                                                    borderBottom: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.06)',
                                                    py: 1.5,
                                                    color: isLight ? '#1E293B' : '#F1F5F9',
                                                }
                                            }}
                                        >
                                            <TableCell sx={{ fontWeight: 600, color: isLight ? '#64748B' : '#94A3B8' }}>
                                                {rowNum}
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 260 }}>
                                                <Typography variant="body2" fontWeight={700} noWrap sx={{ color: isLight ? '#0F172A' : '#FFFFFF' }}>
                                                    {request.item_name}
                                                </Typography>
                                                {request.location && (
                                                    <Box display="flex" alignItems="center" gap={0.5} mt={0.3}>
                                                        <LocationOnIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                                                        <Typography variant="caption" color="text.secondary" noWrap>
                                                            {request.location}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {request.description && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 1,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            mt: 0.2,
                                                        }}
                                                    >
                                                        {stripHtml(request.description)}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={<CategoryIcon sx={{ fontSize: '14px !important' }} />}
                                                    label={request.category || 'General'}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontSize: '0.72rem', height: 24 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={<LocalShippingIcon sx={{ fontSize: '14px !important', color: '#177373 !important' }} />}
                                                    label={request.handover_method || 'Standard'}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ borderColor: '#177373', fontSize: '0.72rem', height: 24 }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ minWidth: { xs: 0, lg: 160 } }}>
                                                {request.app_users ? (
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <Avatar
                                                            sx={{
                                                                width: 30,
                                                                height: 30,
                                                                fontSize: '0.8rem',
                                                                bgcolor: userBlocked ? '#DC2626' : (isLight ? '#E0F2FE' : 'rgba(56, 189, 248, 0.2)'),
                                                                color: userBlocked ? '#FFF' : (isLight ? '#0284C7' : '#38BDF8'),
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            {request.app_users.first_name ? request.app_users.first_name.charAt(0).toUpperCase() : 'U'}
                                                        </Avatar>
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                                <Typography variant="body2" fontWeight={600} noWrap sx={{ color: isLight ? '#1E293B' : '#F1F5F9' }}>
                                                                    {request.app_users.first_name} {request.app_users.last_name}
                                                                </Typography>
                                                                {userBlocked && (
                                                                    <Chip
                                                                        label="Blocked"
                                                                        size="small"
                                                                        sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#DC2626', color: '#FFF', fontWeight: 700, px: 0.3 }}
                                                                    />
                                                                )}
                                                            </Box>
                                                            {request.app_users.email && (
                                                                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                                                    {request.app_users.email}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Unknown
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={request.status}
                                                    size="small"
                                                    color={
                                                        request.status === 'Pending' ? 'warning' :
                                                            request.status === 'Given' ? 'success' :
                                                                request.status === 'Accepted' ? 'info' : 'error'
                                                    }
                                                    sx={{ fontSize: '0.72rem', height: 22, fontWeight: 600 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={request.item_status}
                                                    size="small"
                                                    variant="outlined"
                                                    icon={<VerifiedIcon sx={{ fontSize: '13px !important' }} />}
                                                    color={
                                                        request.item_status === 'Pending' ? 'warning' :
                                                            request.item_status === 'Accepted' ? 'success' :
                                                                request.item_status === 'Declined' ? 'error' : 'info'
                                                    }
                                                    sx={{ fontSize: '0.72rem', height: 22, fontWeight: 600 }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem', color: isLight ? '#64748B' : '#94A3B8' }}>
                                                {new Date(request.created_at).toLocaleDateString()}
                                            </TableCell>
                                            <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                                <Box display="flex" justifyContent="center" alignItems="center" gap={0.5}>
                                                    <Tooltip title="View Details">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleViewRequest(request)}
                                                            sx={{ color: '#4CA1AF', '&:hover': { bgcolor: 'rgba(76, 161, 175, 0.12)' } }}
                                                        >
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>

                                                    {request.item_status === 'Pending' && (
                                                        <>
                                                            <Tooltip title="Accept / Approve">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleUpdateStatus(request.id, 'Accepted')}
                                                                    sx={{ color: '#10B981', '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.12)' } }}
                                                                >
                                                                    <CheckCircleIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Decline / Reject">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleUpdateStatus(request.id, 'Declined')}
                                                                    sx={{ color: '#EF4444', '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.12)' } }}
                                                                >
                                                                    <CancelIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    )}

                                                    {request.item_status === 'Accepted' && (
                                                        <Tooltip title="Decline">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleUpdateStatus(request.id, 'Declined')}
                                                                sx={{ color: '#EF4444', '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.12)' } }}
                                                            >
                                                                <CancelIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {request.item_status === 'Declined' && (
                                                        <Tooltip title="Accept">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleUpdateStatus(request.id, 'Accepted')}
                                                                sx={{ color: '#10B981', '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.12)' } }}
                                                            >
                                                                <CheckCircleIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {request.app_users && (
                                                        <Tooltip title={userBlocked ? "Unblock User" : "Block User"}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleBlockUnblockUser(
                                                                    request.app_users.id,
                                                                    request.app_users.blocked_status,
                                                                    `${request.app_users.first_name} ${request.app_users.last_name}`
                                                                )}
                                                                sx={{
                                                                    color: userBlocked ? '#059669' : '#DC2626',
                                                                    '&:hover': {
                                                                        bgcolor: userBlocked ? 'rgba(5, 150, 105, 0.12)' : 'rgba(220, 38, 38, 0.12)'
                                                                    }
                                                                }}
                                                            >
                                                                <BlockIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                {totalPages > 1 && (
                    <Box display="flex" justifyContent="center" mt={3} width="100%">
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(e, value) => setPage(value)}
                            color="primary"
                        />
                    </Box>
                )}

                {/* View Request Dialog */}
                <Dialog
                    open={openViewDialog}
                    onClose={handleViewDialogClose}
                    fullWidth
                    maxWidth="sm"
                    PaperProps={{
                        sx: {
                            borderRadius: 3,
                            bgcolor: isLight ? '#FFFFFF' : '#1e293b',
                            backgroundImage: 'none',
                            p: 1,
                        }
                    }}
                >
                    <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ color: isLight ? '#0F172A' : '#FFFFFF' }}>
                            Item Request Details
                        </Typography>
                        {selectedRequest && (
                            <Box display="flex" gap={1}>
                                <Chip
                                    label={selectedRequest.status}
                                    size="small"
                                    color={
                                        selectedRequest.status === 'Pending' ? 'warning' :
                                            selectedRequest.status === 'Given' ? 'success' :
                                                selectedRequest.status === 'Accepted' ? 'info' : 'error'
                                    }
                                    sx={{ fontWeight: 600 }}
                                />
                                <Chip
                                    label={`Admin: ${selectedRequest.item_status || 'Pending'}`}
                                    size="small"
                                    variant="outlined"
                                    color={
                                        selectedRequest.item_status === 'Pending' ? 'warning' :
                                            selectedRequest.item_status === 'Accepted' ? 'success' :
                                                selectedRequest.item_status === 'Declined' ? 'error' : 'info'
                                    }
                                    sx={{ fontWeight: 600 }}
                                />
                            </Box>
                        )}
                    </DialogTitle>
                    <DialogContent dividers sx={{ borderColor: isLight ? '#E2E8F0' : 'rgba(255,255,255,0.08)' }}>
                        {selectedRequest && (
                            <Box display="flex" flexDirection="column" gap={2.5}>
                                {/* Item Header */}
                                <Box>
                                    <Typography variant="h6" fontWeight={700} sx={{ color: isLight ? '#0F172A' : '#FFFFFF' }} gutterBottom>
                                        {selectedRequest.item_name}
                                    </Typography>
                                    <Box display="flex" gap={1} flexWrap="wrap">
                                        <Chip
                                            icon={<CategoryIcon sx={{ fontSize: '15px !important' }} />}
                                            label={selectedRequest.category || 'General'}
                                            size="small"
                                            variant="outlined"
                                        />
                                        <Chip
                                            icon={<LocalShippingIcon sx={{ fontSize: '15px !important', color: '#177373 !important' }} />}
                                            label={`Delivery: ${selectedRequest.handover_method || 'Standard'}`}
                                            size="small"
                                            variant="outlined"
                                            sx={{ borderColor: '#177373' }}
                                        />
                                        {selectedRequest.location && (
                                            <Chip
                                                icon={<LocationOnIcon sx={{ fontSize: '15px !important' }} />}
                                                label={selectedRequest.location}
                                                size="small"
                                                variant="outlined"
                                            />
                                        )}
                                    </Box>
                                </Box>

                                {/* Requester Details Card */}
                                {selectedRequest.app_users && (
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            bgcolor: isLight ? '#F8FAFC' : 'rgba(255,255,255,0.03)',
                                            borderColor: isLight ? '#E2E8F0' : 'rgba(255,255,255,0.08)',
                                        }}
                                    >
                                        <Typography variant="caption" fontWeight={700} sx={{ color: isLight ? '#64748B' : '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
                                            Requester Information
                                        </Typography>
                                        <Box display="flex" alignItems="center" gap={1.5}>
                                            <Avatar
                                                sx={{
                                                    width: 44,
                                                    height: 44,
                                                    bgcolor: selectedRequest.app_users.blocked_status === 'yes' ? '#DC2626' : (isLight ? '#E0F2FE' : 'rgba(56, 189, 248, 0.2)'),
                                                    color: selectedRequest.app_users.blocked_status === 'yes' ? '#FFF' : (isLight ? '#0284C7' : '#38BDF8'),
                                                    fontWeight: 700,
                                                    fontSize: '1.1rem'
                                                }}
                                            >
                                                {selectedRequest.app_users.first_name ? selectedRequest.app_users.first_name.charAt(0).toUpperCase() : 'U'}
                                            </Avatar>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: isLight ? '#0F172A' : '#FFFFFF' }}>
                                                        {selectedRequest.app_users.first_name} {selectedRequest.app_users.last_name}
                                                    </Typography>
                                                    {selectedRequest.app_users.blocked_status === 'yes' && (
                                                        <Chip label="Blocked" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#DC2626', color: '#FFF', fontWeight: 700 }} />
                                                    )}
                                                </Box>
                                                {selectedRequest.app_users.email && (
                                                    <Typography variant="body2" color="text.secondary" noWrap>
                                                        {selectedRequest.app_users.email}
                                                    </Typography>
                                                )}
                                                {selectedRequest.app_users.phone_number && (
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        📞 {selectedRequest.app_users.phone_number}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Paper>
                                )}

                                {/* Description Section */}
                                <Box>
                                    <Typography variant="caption" fontWeight={700} sx={{ color: isLight ? '#64748B' : '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.8 }}>
                                        Request Description
                                    </Typography>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            bgcolor: isLight ? '#FFFFFF' : 'rgba(255,255,255,0.02)',
                                            borderColor: isLight ? '#E2E8F0' : 'rgba(255,255,255,0.08)',
                                            maxHeight: 200,
                                            overflowY: 'auto'
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                color: isLight ? '#334155' : 'rgba(255, 255, 255, 0.88)',
                                                lineHeight: 1.6,
                                                fontSize: '0.9rem',
                                                '& p': { m: 0, mb: 0.8, '&:last-child': { mb: 0 } },
                                                '& strong, & b': { color: isLight ? '#0F172A' : '#FFFFFF', fontWeight: 700 },
                                            }}
                                            dangerouslySetInnerHTML={{
                                                __html: DOMPurify.sanitize(selectedRequest.description || 'No description provided.')
                                            }}
                                        />
                                    </Paper>
                                </Box>

                                {/* Timestamps */}
                                <Grid container spacing={1.5}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary">
                                            <strong>Requested On:</strong> {new Date(selectedRequest.created_at).toLocaleString()}
                                        </Typography>
                                    </Grid>
                                    {selectedRequest.updated_at && (
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" color="text.secondary">
                                                <strong>Last Updated:</strong> {new Date(selectedRequest.updated_at).toLocaleString()}
                                            </Typography>
                                        </Grid>
                                    )}
                                    {selectedRequest.given_at && (
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" color="text.secondary">
                                                <strong>Given At:</strong> {new Date(selectedRequest.given_at).toLocaleString()}
                                            </Typography>
                                        </Grid>
                                    )}
                                    {selectedRequest.accepted_at && (
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" color="text.secondary">
                                                <strong>Accepted At:</strong> {new Date(selectedRequest.accepted_at).toLocaleString()}
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: 2, pt: 1.5, gap: 1 }}>
                        {selectedRequest && selectedRequest.item_status === 'Pending' && (
                            <>
                                <Button
                                    onClick={() => {
                                        handleUpdateStatus(selectedRequest.id, 'Accepted');
                                        handleViewDialogClose();
                                    }}
                                    variant="contained"
                                    color="success"
                                    size="small"
                                >
                                    Approve Request
                                </Button>
                                <Button
                                    onClick={() => {
                                        handleUpdateStatus(selectedRequest.id, 'Declined');
                                        handleViewDialogClose();
                                    }}
                                    variant="contained"
                                    color="error"
                                    size="small"
                                >
                                    Decline Request
                                </Button>
                            </>
                        )}
                        <Button onClick={handleViewDialogClose} variant="outlined" color="inherit" size="small">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>



                                {/* Block/Unblock Confirmation Dialog */}
                <Dialog
                    open={openBlockDialog}
                    onClose={() => setOpenBlockDialog(false)}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle>
                        <Typography variant="h6" fontWeight="bold">
                            Confirm {userToBlock?.currentBlockedStatus === 'yes' ? 'Unblock' : 'Block'} User
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" gutterBottom>
                            Are you sure you want to {userToBlock?.currentBlockedStatus === 'yes' ? 'unblock' : 'block'} <strong>{userToBlock?.userName}</strong>?
                        </Typography>
                        {userToBlock?.currentBlockedStatus !== 'yes' && (
                            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                                ⚠️ This will prevent the user from accessing certain features on the platform.
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenBlockDialog(false)} color="inherit">
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmBlockUnblockUser}
                            variant="contained"
                            color={userToBlock?.currentBlockedStatus === 'yes' ? 'success' : 'error'}
                        >
                            {userToBlock?.currentBlockedStatus === 'yes' ? 'Unblock' : 'Block'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Toast Notifications */}
                <Toast toast={toast} setToast={setToast} />

                {/* Module Settings Modal */}
                <ModuleSettingsModal
                    open={settingsOpen}
                    onClose={() => setSettingsOpen(false)}
                    moduleKey="newItemRequests"
                    moduleTitle="Item Requests"
                />
            </Box>
        </>
    );
}
